import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Description as DocumentIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Add as AddIcon,
} from '@mui/icons-material';

const DocumentManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState('root');
  
  const sponsorFolders = [
    { id: 'policies', name: 'Policies & Procedures', icon: <FolderIcon /> },
    { id: 'legal', name: 'Legal Documents', icon: <FolderIcon /> },
    { id: 'guides', name: 'Guides & Resources', icon: <FolderIcon /> },
    { id: 'templates', name: 'Templates', icon: <FolderIcon /> },
  ];

  const projectFolders = [
    { id: 'project-docs', name: 'Project Documents', icon: <FolderIcon /> },
    { id: 'financials', name: 'Financial Documents', icon: <FolderIcon /> },
    { id: 'reports', name: 'Reports', icon: <FolderIcon /> },
    { id: 'contracts', name: 'Contracts & Agreements', icon: <FolderIcon /> },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleUpload = () => {
    setUploadDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper 
        sx={{ 
          backgroundColor: 'rgba(18, 18, 18, 0.95)',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              '&.Mui-selected': {
                color: '#00BCD4'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#00BCD4'
            }
          }}
        >
          <Tab label="Fiscal Sponsor Documents" />
          <Tab label="Project Documents" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 ? (
            <SponsorDocuments 
              folders={sponsorFolders}
              selectedFolder={selectedFolder}
              onFolderSelect={setSelectedFolder}
              onUpload={handleUpload}
            />
          ) : (
            <ProjectDocuments 
              folders={projectFolders}
              selectedFolder={selectedFolder}
              onFolderSelect={setSelectedFolder}
              onUpload={handleUpload}
            />
          )}
        </Box>
      </Paper>

      <UploadDialog 
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        folder={selectedFolder}
      />
    </Box>
  );
};

const SponsorDocuments = ({ folders, selectedFolder, onFolderSelect, onUpload }) => {
  return (
    <Box sx={{ display: 'flex', gap: 3 }}>
      {/* Folder Navigation */}
      <Box sx={{ width: 280 }}>
        <List>
          {folders.map((folder) => (
            <ListItem
              key={folder.id}
              button
              selected={selectedFolder === folder.id}
              onClick={() => onFolderSelect(folder.id)}
              sx={{
                borderRadius: 1,
                mb: 1,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(0, 188, 212, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 188, 212, 0.2)',
                  }
                }
              }}
            >
              <ListItemIcon sx={{ color: '#00BCD4' }}>
                {folder.icon}
              </ListItemIcon>
              <ListItemText 
                primary={folder.name}
                sx={{ color: '#fff' }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Document List */}
      <Box sx={{ flex: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Documents
          </Typography>
          <Button
            startIcon={<UploadIcon />}
            onClick={onUpload}
            sx={{
              backgroundColor: 'rgba(0, 188, 212, 0.1)',
              color: '#00BCD4',
              '&:hover': {
                backgroundColor: 'rgba(0, 188, 212, 0.2)',
              }
            }}
          >
            Upload Document
          </Button>
        </Box>

        <DocumentList folder={selectedFolder} />
      </Box>
    </Box>
  );
};

// ProjectDocuments component is similar to SponsorDocuments
const ProjectDocuments = SponsorDocuments;

const DocumentList = ({ folder }) => {
  // This would be populated from your backend
  const documents = [
    { id: 1, name: 'Sample Document.pdf', type: 'pdf', date: '2024-03-07' },
    // ... more documents
  ];

  return (
    <List>
      {documents.map((doc) => (
        <ListItem
          key={doc.id}
          sx={{
            borderRadius: 1,
            mb: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        >
          <ListItemIcon sx={{ color: '#00BCD4' }}>
            <DocumentIcon />
          </ListItemIcon>
          <ListItemText 
            primary={doc.name}
            secondary={doc.date}
            sx={{ 
              '& .MuiListItemText-primary': { color: '#fff' },
              '& .MuiListItemText-secondary': { color: 'rgba(255, 255, 255, 0.7)' }
            }}
          />
          <ListItemSecondaryAction>
            <Tooltip title="Download">
              <IconButton sx={{ color: '#00BCD4' }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton sx={{ color: '#ff4444' }}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </List>
  );
};

const UploadDialog = ({ open, onClose, folder }) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(18, 18, 18, 0.95)',
          color: '#fff',
          minWidth: 400
        }
      }}
    >
      <DialogTitle>Upload Document</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Document Name"
          fullWidth
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: '#fff',
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#00BCD4',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              '&.Mui-focused': {
                color: '#00BCD4',
              },
            },
          }}
        />
        <Button
          component="label"
          startIcon={<UploadIcon />}
          sx={{ mt: 2, color: '#00BCD4' }}
        >
          Choose File
          <input type="file" hidden />
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Cancel
        </Button>
        <Button 
          onClick={onClose}
          sx={{
            backgroundColor: 'rgba(0, 188, 212, 0.1)',
            color: '#00BCD4',
            '&:hover': {
              backgroundColor: 'rgba(0, 188, 212, 0.2)',
            }
          }}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentManagement; 